import React from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { Download, BarChart3, Users, DollarSign, Activity, Eye, FileText } from 'lucide-react';

const ReportViewer = ({ reportData, onExport }) => {
    const { t } = useLanguage();

    const calculateSummaryStats = () => {
        const { type, report } = reportData;

        switch (type) {
            case 'users':
                return {
                    totalNewUsers: report.reduce((sum, day) => sum + (day.new_users || 0), 0),
                    totalPremium: report.reduce((sum, day) => sum + (day.premium_users || 0), 0),
                    peakDay: report.reduce((max, day) => day.new_users > (max?.new_users || 0) ? day : max, report[0])
                };
            case 'revenue':
                return {
                    totalRevenue: report.reduce((sum, day) => sum + (day.daily_revenue || 0), 0),
                    totalPayments: report.reduce((sum, day) => sum + (day.payment_count || 0), 0),
                    averagePayment: report.reduce((sum, day) => sum + (day.daily_revenue || 0), 0) /
                        report.reduce((sum, day) => sum + (day.payment_count || 0), 1)
                };
            case 'usage':
                return {
                    totalSessions: report.reduce((sum, day) => sum + (day.session_count || 0), 0),
                    maxActiveUsers: Math.max(...report.map(day => day.active_users || 0)),
                    totalTokens: report.reduce((sum, day) => sum + (day.ai_tokens || 0), 0)
                };
            default:
                return {};
        }
    };

    const summaryStats = calculateSummaryStats();

    const renderReportContent = () => {
        const { type, report } = reportData;

        const StatCard = ({ icon, value, label, subtitle }) => (
            <div className="stat-card">
                <div className="stat-icon">{icon}</div>
                <div className="stat-content">
                    <div className="stat-value">{value}</div>
                    <div className="stat-label">{label}</div>
                    {subtitle && <div className="stat-subtitle">{subtitle}</div>}
                </div>
            </div>
        );

        return (
            <div className="report-content">
                <div className="summary-section">
                    <h3>{t('reportingDataSummary')}</h3>
                    <div className="summary-stats">
                        {type === 'users' && (
                            <>
                                <StatCard
                                    icon={<Users size={24} />}
                                    value={summaryStats.totalNewUsers}
                                    label={t('reportingNewUserStats')}
                                    subtitle="Toplam yeni kullanıcı"
                                />
                                <StatCard
                                    icon={<Activity size={24} />}
                                    value={summaryStats.totalPremium}
                                    label={t('reportingPremiumUserStats')}
                                    subtitle="Premium kullanıcı sayısı"
                                />
                                <StatCard
                                    icon={<Eye size={24} />}
                                    value={summaryStats.peakDay?.new_users || 0}
                                    label="En Yoğun Gün"
                                    subtitle={summaryStats.peakDay?.date}
                                />
                            </>
                        )}
                        {type === 'revenue' && (
                            <>
                                <StatCard
                                    icon={<DollarSign size={24} />}
                                    value={`${summaryStats.totalRevenue.toFixed(2)} TL`}
                                    label={t('reportingDailyRevenue')}
                                    subtitle="Toplam gelir"
                                />
                                <StatCard
                                    icon={<BarChart3 size={24} />}
                                    value={summaryStats.totalPayments}
                                    label={t('reportingPaymentCount')}
                                    subtitle="Toplam ödeme sayısı"
                                />
                                <StatCard
                                    icon={<Activity size={24} />}
                                    value={`${summaryStats.averagePayment.toFixed(2)} TL`}
                                    label={t('reportingAveragePayment')}
                                    subtitle="Ortalama ödeme tutarı"
                                />
                            </>
                        )}
                        {type === 'usage' && (
                            <>
                                <StatCard
                                    icon={<Activity size={24} />}
                                    value={summaryStats.totalSessions}
                                    label={t('reportingSessionCount')}
                                    subtitle="Toplam oturum sayısı"
                                />
                                <StatCard
                                    icon={<Users size={24} />}
                                    value={summaryStats.maxActiveUsers}
                                    label={t('reportingActiveUserCount')}
                                    subtitle="Maksimum aktif kullanıcı"
                                />
                                <StatCard
                                    icon={<BarChart3 size={24} />}
                                    value={summaryStats.totalTokens.toLocaleString()}
                                    label={t('reportingAITokenUsage')}
                                    subtitle="Toplam AI token kullanımı"
                                />
                            </>
                        )}
                    </div>
                </div>

                <div className="detailed-section">
                    <h3>{t('reportingDetailedView')}</h3>
                    <div className="data-table">
                        <table>
                            <thead>
                                <tr>
                                    {report[0] && Object.keys(report[0]).map(key => (
                                        <th key={key}>{key}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {report.map((row, index) => (
                                    <tr key={index}>
                                        {Object.values(row).map((value, cellIndex) => (
                                            <td key={cellIndex}>
                                                {typeof value === 'number' ? value.toLocaleString() : value}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="report-viewer-card">
            <div className="viewer-header">
                <div className="header-main">
                    <FileText size={24} />
                    <div>
                        <h2>{t('reportingDataPreview')}</h2>
                        <p>
                            {reportData.start_date} - {reportData.end_date} | {t(`reporting${reportData.type.charAt(0).toUpperCase() + reportData.type.slice(1)}Reports`)}
                        </p>
                    </div>
                </div>
                <div className="header-actions">
                    <button
                        className="export-button"
                        onClick={() => onExport('csv')}
                    >
                        <Download size={16} />
                        {t('reportingDownloadCSV')}
                    </button>
                    <button
                        className="export-button secondary"
                        onClick={() => onExport('pdf')}
                    >
                        <FileText size={16} />
                        {t('reportingDownloadPDF')}
                    </button>
                </div>
            </div>

            {renderReportContent()}
        </div>
    );
};

export default ReportViewer;